import React, { useState, useEffect, useRef, memo } from 'react';
import { Search } from 'lucide-react';

/**
 * Fast search input with internal debounce.
 * - Controlled internally (so backspace-to-empty is always seen by React)
 * - Debounces `onChange` to parent (default 200ms)
 * - Escape clears the input + flushes the empty string immediately
 * - Memoized so parent re-renders don't blow away the local state
 */
const DebouncedSearchInput = memo(function DebouncedSearchInput({
  initialValue = '',
  onChange,
  placeholder = 'Buscar...',
  delay = 200,
  className = '',
  'data-testid': testId,
}) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  // Skip the very first debounce emit: it fires with the hydrated
  // `initialValue`, and the parent already knows that value. Emitting anyway
  // makes the parent think the user typed, which — combined with a common
  // `setCurrentPage(1)` in the parent's onChange — resets pagination every
  // time the component remounts (e.g. after a delete triggers `loading` to
  // toggle and re-mount the tab tree).
  const firstEmitSkipped = useRef(false);

  // Fire onChange to parent (debounced). Empty string included — that's how the
  // parent knows to show the full list again after the user clears the search.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!firstEmitSkipped.current) {
        firstEmitSkipped.current = true;
        return;
      }
      onChangeRef.current?.(value);
    }, delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setValue(''); }
  };

  return (
    <div className="relative w-full">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
        className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pl-9 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm ${className}`}
      />
    </div>
  );
});

export default DebouncedSearchInput;
